/**
 * Prime sei lettere del codice fiscale di una persona (tre del cognome, tre del nome): servono a dire se nome e
 * cognome dei dati portale sono invertiti, cosa che il portale Ermes rifiuta a pagina 3 ("Il nome e il cognome non
 * sono congruenti con il codice fiscale"). Stesse regole del backend (FiscalCodeNames).
 */
const VOWELS = 'AEIOU'

/** Solo lettere A-Z maiuscole, senza accenti, spazi e apostrofi. */
export function cfLetters(s: string | undefined): string {
  return (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z]/g, '')
}

export function cfSurnameCode(surname: string | undefined): string {
  const l = cfLetters(surname)
  const cons = [...l].filter(c => !VOWELS.includes(c)).join('')
  const vow = [...l].filter(c => VOWELS.includes(c)).join('')
  return (cons + vow + 'XXX').slice(0, 3)
}

export function cfNameCode(name: string | undefined): string {
  const l = cfLetters(name)
  const cons = [...l].filter(c => !VOWELS.includes(c)).join('')
  if (cons.length >= 4) return cons[0]! + cons[2]! + cons[3]!
  const vow = [...l].filter(c => VOWELS.includes(c)).join('')
  return (cons + vow + 'XXX').slice(0, 3)
}

const PERSON_CF = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[A-EHLMPR-T][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/

export type CfNamesCheck = 'unknown' | 'ok' | 'swapped' | 'mismatch'

/** Confronta il codice fiscale con nome e cognome: 'unknown' se non è il codice di una persona o mancano i nomi. */
export function cfNamesCheck(fiscalCode: string | undefined, nome: string | undefined, cognome: string | undefined): CfNamesCheck {
  const cf = (fiscalCode ?? '').trim().toUpperCase()
  if (!PERSON_CF.test(cf) || !cfLetters(nome) || !cfLetters(cognome)) return 'unknown'
  const head = cf.slice(0, 6)
  if (head === cfSurnameCode(cognome) + cfNameCode(nome)) return 'ok'
  if (head === cfSurnameCode(nome) + cfNameCode(cognome)) return 'swapped'
  return 'mismatch'
}

/**
 * Nome e cognome da un nome scritto tutto insieme («Mario Rossi» oppure «Rossi Mario», anche con più parole), decisi dal
 * codice fiscale quando c'è: si prova ogni punto di divisione nei due ordini e vince quello coerente con il codice.
 * Senza codice, o senza una divisione coerente, la prima parola è il nome e il resto il cognome (come prima).
 * 30/10/2026: il rappresentante legale arriva dalla visura come «OLIVA CLAUDIA FRANCESCA» e il portale Ermes rifiutava
 * il firmatario del conto perché il cognome era finito nel nome.
 */
export function splitNameByCf(full: string | undefined, fiscalCode: string | undefined): { nome: string; cognome: string } {
  const words = (full ?? '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return { nome: '', cognome: '' }
  const fallback = { nome: words[0]!, cognome: words.slice(1).join(' ') }
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(' ')
    const b = words.slice(i).join(' ')
    if (cfNamesCheck(fiscalCode, a, b) === 'ok') return { nome: a, cognome: b }
    if (cfNamesCheck(fiscalCode, b, a) === 'ok') return { nome: b, cognome: a }
  }
  return fallback
}
